import React, { useState, useEffect, useCallback } from "react";
import { dataGridSurface } from "../common/tableStyles";
import { Box, styled, Avatar } from "@mui/material";
import { DataGrid, GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import CategoryService from "../../services/CategoryService";
import ICategory from "../../entities/ICategory";
import CategoryActions from "./CategoryActions";
import { unwrapList, resolveImageUrl } from "../../services/apiUtils";
import useCategoryLookup, { invalidateCategoryLookup } from "../../hooks/useCategoryLookup";
import DataGridLoadingOverlay from "../common/DataGridLoadingOverlay";


const StyledTable = styled(Box)(({ theme }) => ({
  ...dataGridSurface(theme),
  margin: "20px 0 0 0",
  height: 560,
}));

const ListCategory: React.FC = () => {
  const [categoryData, setCategoryData] = useState<Array<ICategory>>([]);
  const categoryLookup = useCategoryLookup();
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

  const loadCategories = useCallback(() => {
    setLoading(true);
    invalidateCategoryLookup();
    CategoryService.getCategory()
      .then((res: any) => {
        setCategoryData(unwrapList<ICategory>(res));
      })
      .catch(() => {
        setCategoryData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const columns: GridColDef[] = [
    { field: "categoryId", headerName: "ID" },
    {
      field: "thumbnail",
      headerName: "Image",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar
          variant="rounded"
          src={resolveImageUrl(params.row.thumbnail) || undefined}
          sx={{ width: 36, height: 36, bgcolor: "#EEF0FB" }}
        />
      ),
    },
    {
      field: "title",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "description",
      headerName: "Description",
      type: "string",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "parentId",
      headerName: "Parent Category",
      type: "string",
      flex: 1,
      valueGetter: (params) =>
        params.row?.parentId ? categoryLookup[params.row.parentId] || `#${params.row.parentId}` : "-",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 170,
      type: "actions",
      renderCell: (params) => (
        <CategoryActions
          category={params.row}
          parentName={categoryLookup[params.row.parentId]}
          onDeleted={loadCategories}
        />
      ),
    },
  ];
  return (
    <StyledTable>
      <DataGrid
        sx={{ backgroundColor: "transparent" }}
        rows={categoryData}
        columns={columns}
        getRowId={(row: any) => row.categoryId}
        components={{ LoadingOverlay: DataGridLoadingOverlay }}
        loading={loading}
        pageSize={30}
        rowsPerPageOptions={[30]}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
      />
    </StyledTable>
  );
};

export default ListCategory;
